import type { TopologyPayload } from "./types/topology";
import type { ViewMode } from "./viewMode";


type Seat       = TopologyPayload["seats"][string];
type Layout     = TopologyPayload["layout"];
type TopologyCfg = Layout["topology"];


export const ROW_GAP        = 4;
export const BANNER_H       = 30;
export const LETTER_STRIP_H = 22;
export const COL_LETTER_FS  = 16;
export const BANNER_BG      = "#F1F5F9";

export const CS_Y           = 18;
export const CS_H           = 40;
export const NC_Y_EX3       = 92;
export const NC_H_EX3       = 54;
// Socket grammar (v3): every port chip STRADDLES its card's border — half
// inside, half outside — like a physical socket punched into the housing.
// Out-ports sit on the bottom edge, in-ports on the top edge, one uniform
// chip size everywhere (MCU / NC / FS / AI / ADB ports alike).
export const PORT_H         = 20;
export const PORT_W_EX3     = 28;
export const PORT_Y_EX3     = NC_Y_EX3 + NC_H_EX3 - PORT_H / 2;
export const HEADER_H_EX3   = PORT_Y_EX3 + PORT_H + 64;

export const FS_Y_EX1       = 18;
// Card heights leave clear "socket strips" top AND bottom so straddling
// chips never touch the title or type-code lines.
export const FS_H_EX1       = 56;  // title + type-code only (names live in the right pane)
// FS-child HeadEnd LRUs (e.g. ADBs with Server_Type=FS) render in a thinner
// row just below the primary HeadEnd to convey "auxiliary, hangs off FS".
export const SUB_Y_EX1      = 72;
export const SUB_H_EX1      = 44;  // title-only (uplink is drawn, not written)
export const MCU_Y_EX1      = 116;
export const MCU_H_EX1      = 54;
export const PORT_Y_EX1     = MCU_Y_EX1 + MCU_H_EX1 - PORT_H / 2;

export const CHIP_H = 16;
export const TAB_W  = 28;
export const TAB_H  = 16;
// Micro-socket style: near-black chip seated into the card edge with a
// light bezel rim — one look for every out-socket and entry tab.
export const SOCKET_FILL   = "#0F172A";
export const SOCKET_STROKE = "#B6C2D1";

// Uniform hierarchy-connector style — identical across all fleets and tiers
// (CS→NC, NC→port, FS→uplink→ADB, parent→sub, MCU→port). Highlight styling
// lives in ConnectorLine (TopTierRails) and is likewise fleet-independent.
export const CONN_STROKE  = "#94A3B8";
export const CONN_WIDTH   = 1.2;
export const CONN_OPACITY = 0.55;

export const PANEL_GAP       = 28;
export const PANEL_W         = 310;
export const PANEL_RIGHT_PAD = 20;
export const BOTTOM_PAD      = 110;
export const ZONE_BOX_H      = 104;


export interface CabinGrid {
  colX: Record<string, number>;
  aisles: number[];
}

export interface RowDescriptor {
  rn: number;
  cabinName: string;
  cabinLetters: string[];
}

export interface CabinSection {
  cabinName: string;
  bannerY: number;
  rowsYStart: number;
  seatCount: number;
}

export interface Box {
  x: number;
  y: number;
  width: number;
  height: number;
  fill: string;
  stroke: string;
  primaryLabel: string;
  secondaryLabel?: string;
  /** "leaf" = terminal branch off a server (CT / MAS) — not selectable;
   *  ref.primary carries the PARENT AI number so it highlights with it.
   *  "aiport" = outgoing port chip under a top-row parent (AI) — every
   *  uplink leaves through one so port numbers never float mid-line.
   *  "inport" = incoming-port tab on a child's top edge; selection and
   *  highlighting delegate to the host box via hostKind. */
  kind: "cs" | "nc" | "fs" | "ai" | "mcu" | "port" | "adb" | "dport" | "uport" | "leaf" | "aiport" | "inport";
  /** For ports: parent (nc, port) or (mcu, port). Otherwise just primary. */
  ref: { primary: number; secondary?: number };
  /** For "inport": the kind whose selection/highlight semantics apply. */
  hostKind?: "cs" | "nc" | "fs" | "ai" | "mcu" | "port" | "adb" | "dport" | "uport" | "leaf" | "aiport";
  /** For "aiport" / "leaf": the parent unit's type ("AI", "IS", …) so
   *  highlighting can match uplink triples exactly. */
  parentKind?: string;
  /** Restrict the box to one view (chips/tabs are built per view).
   *  "both" forces visibility in both views (e.g. an IS server that is
   *  also the MCUs' power parent). */
  view?: "data" | "mcu" | "both";
}

export interface Connector {
  x1: number; y1: number;
  x2: number; y2: number;
  stroke: string;
  width: number;
  opacity?: number;
  /** "link" = iLOPA_Flag secondary edge (ADB ring / FS cross-link / dual
   *  parent), drawn dashed. "power" = AI→MCU uplink, MCU view only. */
  conn: "cs" | "port" | "hop" | "sub" | "chain" | "link" | "power";
  nc?: number;
  port?: number;
  /** Rendered as a <path> (dashed arcs between same-row boxes). x1/y1/x2/y2
   *  still carry the endpoints for the no-hanging-lines check. */
  path?: string;
  dashed?: boolean;
}

/** Small port annotation pinned to a connector end (from lru_lookup_ilopa:
 *  parent-side Server_Port and the LRU's own incoming LRU_Port). */
export interface ConnLabel {
  x: number;
  y: number;
  text: string;
  view: "data" | "mcu";
  anchor?: "start" | "middle" | "end";
  /** "redundant" = the label belongs to an iLOPA_Flag cable — tinted amber
   *  to match the dashed line it annotates. */
  tone?: "redundant";
}

export interface FleetGeometry {
  rows: RowDescriptor[];
  sections: CabinSection[];
  cabinGrids: Map<string, CabinGrid>;
  rowYs: Map<number, number>;
  gridLeft: number;
  masterGridW: number;
  gridRight: number;
  seatGridYStart: number;
  seatGridHeight: number;

  headerH: number;
  topTierBoxes: Box[];
  portBoxes: Box[];
  connectors: Connector[];
  /** Port annotations on connector ends (v2 / lru_lookup_ilopa fleets). */
  labels: ConnLabel[];
  /** Port column centre by routing key (e.g. "1-8" for eX3, "0-1" for eX1). */
  portCentreByKey: Record<string, number>;
  /**
   * Data-chain colour by key "FS1-p3" / "ADB2-p4" / "IS1-p8" — one colour per
   * distinct (parent, port) chain parsed from the corrected SEB_Port_Mapping.
   * Empty for eX3 (zone_colors already encode the chain) and for fleets
   * without chain data.
   */
  chainColorByKey: Record<string, string>;

  panelX: number;
  panelW: number;

  fuselageLeft: number;
  fuselageRight: number;

  // viewBoxX can be negative on eX1 fleets whose rails extend left past x=0.
  viewBoxX: number;
  totalWidth: number;
  totalHeight: number;
}


// Premium categorical palette for data-chain colouring (Data view, eX1).
// Ordered for maximum adjacent contrast; assignment is stable by sorted
// (parent kind, parent number, port) so colours don't shuffle between loads.
// All entries are deep enough to carry white text (luminance < 155) so seat
// typography stays uniform across fleets/zones; isLightColor() remains as a
// guard for JSON-driven zone colours outside this palette.
const CHAIN_PALETTE = [
  "#3B82F6", "#8B5CF6", "#10B981", "#D97706",
  "#EF4444", "#06B6D4", "#EC4899", "#65A30D",
  "#6366F1", "#14B8A6", "#EA580C", "#A855F7",
];

/**
 * Rounded orthogonal cable: drop from (x1,y1), run horizontally along laneY,
 * drop into (x2,y2). Collapses to a straight line when the ends align.
 */
export function orthPath(
  x1: number, y1: number, x2: number, y2: number, laneY: number, r = 6,
): string {
  if (Math.abs(x1 - x2) < 2) return `M ${x1} ${y1} L ${x2} ${y2}`;
  const dir = x2 > x1 ? 1 : -1;
  const d1 = laneY > y1 ? 1 : -1;  // leg 1: y1 → laneY
  const d2 = y2 > laneY ? 1 : -1;  // leg 2: laneY → y2
  const rr = Math.min(
    r,
    Math.abs(x2 - x1) / 2,
    Math.abs(laneY - y1),
    Math.abs(y2 - laneY),
  );
  return [
    `M ${x1} ${y1}`,
    `L ${x1} ${laneY - d1 * rr}`,
    `Q ${x1} ${laneY} ${x1 + dir * rr} ${laneY}`,
    `L ${x2 - dir * rr} ${laneY}`,
    `Q ${x2} ${laneY} ${x2} ${laneY + d2 * rr}`,
    `L ${x2} ${y2}`,
  ].join(" ");
}

/**
 * Greedy interval lane allocation for the horizontal runs of orthogonal
 * cables sharing one channel: overlapping x-spans get different lanes so
 * their horizontal segments never sit on top of each other. Vertical-only
 * cables (|x1−x2| < 2) don't occupy a lane. Returns a lane index per span.
 */
export function assignLanes(
  spans: Array<{ x1: number; x2: number }>,
  minGap = 14,
): number[] {
  const lanes: number[] = new Array(spans.length).fill(0);
  const order = spans
    .map((_, i) => i)
    .filter((i) => Math.abs(spans[i].x1 - spans[i].x2) >= 2)
    .sort((a, b) =>
      Math.min(spans[a].x1, spans[a].x2) - Math.min(spans[b].x1, spans[b].x2));
  const laneEnds: number[] = [];
  for (const i of order) {
    const lo = Math.min(spans[i].x1, spans[i].x2);
    const hi = Math.max(spans[i].x1, spans[i].x2);
    let lane = laneEnds.findIndex((end) => end < lo - minGap);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(hi);
    } else {
      laneEnds[lane] = hi;
    }
    lanes[i] = lane;
  }
  return lanes;
}

/** Perceived-luminance check — decides dark-on-light vs white-on-dark text. */
export function isLightColor(hex: string): boolean {
  if (!hex.startsWith("#") || hex.length < 7) return false;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return 0.299 * r + 0.587 * g + 0.114 * b > 155;
}

/** Chain key for a seat, or null when it has no parsed data chain. */
export function chainKey(info: Seat): string | null {
  if (!info.chain_kind || info.chain_num === undefined || info.chain_port === undefined) {
    return null;
  }
  return `${info.chain_kind}${info.chain_num}-p${info.chain_port}`;
}

/** Stable colour per distinct data chain — same assignment everywhere
 *  (SVG seats, chain dock, fleet overview): sorted keys → palette order. */
export function buildChainColors(
  seats: TopologyPayload["seats"],
): Record<string, string> {
  const out: Record<string, string> = {};
  const keys = new Set<string>();
  for (const s of Object.values(seats)) {
    const k = chainKey(s);
    if (k) keys.add(k);
  }
  [...keys].sort().forEach((k, i) => {
    out[k] = CHAIN_PALETTE[i % CHAIN_PALETTE.length];
  });
  return out;
}


export function buildFleetGeometry(
  topology: TopologyPayload,
  viewMode: ViewMode = "data",
): FleetGeometry {
  const { layout, seats } = topology;
  const canvas = layout.canvas;
  const topo: TopologyCfg = layout.topology;
  const isEx3 = topo.type === "nc-port";

  const masterCols    = layout.columns.master;
  const nSeatsMaster  = masterCols.filter((c) => c !== "|").length;
  const nAislesMaster = masterCols.filter((c) => c === "|").length;
  const masterGridW   =
    nSeatsMaster * canvas.cell_w + nAislesMaster * canvas.gap_aisle;
  const gridLeft  = canvas.left_pad + canvas.cabin_col_offset;
  const gridRight = gridLeft + masterGridW;

  const seatsByRow = new Map<number, Seat[]>();
  for (const info of Object.values(seats)) {
    let bucket = seatsByRow.get(info.row);
    if (!bucket) {
      bucket = [];
      seatsByRow.set(info.row, bucket);
    }
    bucket.push(info);
  }
  const rns = [...seatsByRow.keys()].sort((a, b) => a - b);

  const rows: RowDescriptor[] = rns.map((rn) => {
    const first = seatsByRow.get(rn)![0];
    const cabinName = first.cabin;
    const cabinLayout = layout.cabins[cabinName]?.layout ?? masterCols;
    const cabinLetters = cabinLayout.filter((c) => c !== "|");
    return { rn, cabinName, cabinLetters };
  });

  const cabinsInUse = new Set(rows.map((r) => r.cabinName));
  const cabinGrids = new Map<string, CabinGrid>();
  for (const cabin of cabinsInUse) {
    cabinGrids.set(cabin, buildCabinGrid(cabin, layout, masterGridW, gridLeft));
  }

  // Stable colour per distinct data chain (see FleetGeometry.chainColorByKey).
  // Computed before the top tier so chain rails can adopt the same colours.
  const chainColorByKey = buildChainColors(seats);

  let topTierBoxes: Box[] = [];
  let portBoxes: Box[]    = [];
  let connectors: Connector[] = [];
  let portCentreByKey: Record<string, number> = {};
  let labels: ConnLabel[] = [];
  let headerH: number;

  const cabinBandX = canvas.cabin_band_x ?? canvas.cabin_col_offset;
  const fuselageLeft  = canvas.left_pad + cabinBandX - 20;
  const fuselageRight = gridRight + 20;

  if (isEx3) {
    ({ topTierBoxes, portBoxes, connectors, portCentreByKey, headerH } =
      buildEx3TopTier(topo, fuselageLeft, fuselageRight));
  } else {
    ({ topTierBoxes, portBoxes, connectors, portCentreByKey, headerH, labels } =
      buildEx1TopTier(seats, topo, fuselageLeft, fuselageRight, topology.lrus ?? [], viewMode,
                      chainColorByKey, topology.lru_links ?? []));
  }

  const seatGridYStart = headerH;

  const sections: CabinSection[] = [];
  const rowYs = new Map<number, number>();
  let y = seatGridYStart;
  let prevCabin: string | null = null;
  for (const r of rows) {
    if (r.cabinName !== prevCabin) {
      sections.push({
        cabinName: r.cabinName,
        bannerY: y,
        rowsYStart: y + BANNER_H + LETTER_STRIP_H,
        seatCount: 0,
      });
      y += BANNER_H + LETTER_STRIP_H;
      prevCabin = r.cabinName;
    }
    rowYs.set(r.rn, y);
    y += canvas.cell_h + ROW_GAP;
    sections[sections.length - 1].seatCount += seatsByRow.get(r.rn)!.length;
  }
  const seatGridYEnd = y;
  const seatGridHeight = seatGridYEnd - seatGridYStart;

  const panelX = gridRight + PANEL_GAP;
  const panelW = PANEL_W;

  // eX1 FS/AI rails can extend left past x=0 on wide fleets — shift viewBox so nothing clips.
  const allLeftEdges = [
    0,
    fuselageLeft - 4,
    ...topTierBoxes.map((b) => b.x),
    ...portBoxes.map((b) => b.x),
    ...connectors.map((c) => Math.min(c.x1, c.x2)),
  ];
  const minLeft = Math.min(...allLeftEdges);
  const viewBoxX = Math.min(0, minLeft - 8);

  // Zone cards render as HTML in the right panel now — the canvas is just
  // the aircraft + head-end tree. Width covers the widest drawn element
  // (76K's head-end tree extends past the fuselage on the right).
  const allRightEdges = [
    fuselageRight + 20,
    ...topTierBoxes.map((b) => b.x + b.width),
    ...portBoxes.map((b) => b.x + b.width),
    ...connectors.map((c) => Math.max(c.x1, c.x2)),
  ];
  const rawRight   = Math.max(...allRightEdges) + 8;
  const totalWidth = rawRight - viewBoxX;
  const totalHeight = seatGridYEnd + BOTTOM_PAD;

  return {
    rows, sections, cabinGrids, rowYs,
    gridLeft, masterGridW, gridRight,
    seatGridYStart, seatGridHeight,
    headerH,
    topTierBoxes, portBoxes, connectors, portCentreByKey, labels,
    chainColorByKey,
    panelX, panelW,
    fuselageLeft, fuselageRight,
    viewBoxX, totalWidth, totalHeight,
  };
}


function buildEx3TopTier(
  topo: TopologyCfg,
  fuselageLeft: number,
  fuselageRight: number,
) {
  const fuseW = fuselageRight - fuselageLeft;
  const ncCenterGap = 32;
  const ncWCap = 270;
  const ncW = Math.min(ncWCap, (fuseW - ncCenterGap - 48) / 2);
  const topoTotalW = 2 * ncW + ncCenterGap;
  const topoLeftPad = (fuseW - topoTotalW) / 2;
  const nc1X = fuselageLeft + topoLeftPad;
  const nc2X = nc1X + ncW + ncCenterGap;

  const csW  = Math.min(150, ncW * 0.60);
  const cs1X = nc1X + ncW / 2 - csW / 2;
  const cs2X = nc2X + ncW / 2 - csW / 2;

  const csBoxes: Box[] = [
    { x: cs1X, y: CS_Y, width: csW, height: CS_H, fill: "#33689E", stroke: "#1F4265",
      primaryLabel: "CS 001", secondaryLabel: "336-002",
      kind: "cs", ref: { primary: 1 } },
    { x: cs2X, y: CS_Y, width: csW, height: CS_H, fill: "#33689E", stroke: "#1F4265",
      primaryLabel: "CS 002", secondaryLabel: "336-002",
      kind: "cs", ref: { primary: 2 } },
  ];
  const ncBoxes: Box[] = [
    { x: nc1X, y: NC_Y_EX3, width: ncW, height: NC_H_EX3, fill: "#B03A2E", stroke: "#6E241C",
      primaryLabel: "NC 1 · 370-001", secondaryLabel: "+ NC_AM35X 001 · 371-001",
      kind: "nc", ref: { primary: 1 } },
    { x: nc2X, y: NC_Y_EX3, width: ncW, height: NC_H_EX3, fill: "#1E8449", stroke: "#0F4D28",
      primaryLabel: "NC 2 · 370-001", secondaryLabel: "+ NC_AM35X 002 · 371-001",
      kind: "nc", ref: { primary: 2 } },
  ];

  // CS→NC is a full 2×2 mesh. Own-column pairs drop straight; the two cross
  // cables leave/enter offset from the box centres and run separate lanes,
  // so no two cables ever share an endpoint or a horizontal run.
  const connectors: Connector[] = [];
  {
    const csx = (i: number) => (i === 1 ? cs1X : cs2X) + csW / 2;
    const ncx = (i: number) => (i === 1 ? nc1X : nc2X) + ncW / 2;
    const chTop = CS_Y + CS_H;
    let crossLane = 0;
    for (let cs = 1; cs <= 2; cs++) {
      for (let nc = 1; nc <= 2; nc++) {
        const cross = cs !== nc;
        const dir = nc === 2 ? 1 : -1;  // travel direction of a cross cable
        const x1 = csx(cs) + (cross ? dir * 16 : 0);
        const x2 = ncx(nc) - (cross ? dir * 26 : 0);
        const laneY = chTop + 12 + (cross ? crossLane++ : 0) * 10;
        connectors.push({
          x1, y1: chTop, x2, y2: NC_Y_EX3,
          stroke: CONN_STROKE, width: CONN_WIDTH, opacity: CONN_OPACITY,
          conn: "cs", nc,
          path: orthPath(x1, chTop, x2, NC_Y_EX3, laneY, 7),
        });
      }
    }
  }

  const nc1Ports = topo.nc1_ports ?? [];
  const nc2Ports = topo.nc2_ports ?? [];
  const portW = PORT_W_EX3;
  const portGapNc1 = (ncW - nc1Ports.length * portW) / (nc1Ports.length + 1);
  const portGapNc2 = (ncW - nc2Ports.length * portW) / (nc2Ports.length + 1);
  const portBoxes: Box[] = [];
  const portCentreByKey: Record<string, number> = {};

  // Ports are sockets flush on the NC's bottom edge — the containment
  // already reads "these ports belong to this NC"; no fan lines needed.
  const addPorts = (nc: number, plist: number[], baseX: number, gap: number) => {
    plist.forEach((port, i) => {
      const cx = baseX + gap + i * (portW + gap) + portW / 2;
      const colour = topo.zone_colors[`${nc}-${port}`] ?? "#888888";
      portBoxes.push({
        x: cx - portW / 2, y: PORT_Y_EX3, width: portW, height: PORT_H,
        fill: colour, stroke: "#1E293B",
        primaryLabel: `P${port}`, secondaryLabel: "",
        kind: "port", ref: { primary: nc, secondary: port },
      });
      portCentreByKey[`${nc}-${port}`] = cx;
    });
  };
  addPorts(1, nc1Ports, nc1X, portGapNc1);
  addPorts(2, nc2Ports, nc2X, portGapNc2);

  return {
    topTierBoxes: [...csBoxes, ...ncBoxes],
    portBoxes,
    connectors,
    portCentreByKey,
    labels: [] as ConnLabel[],
    headerH: HEADER_H_EX3,
  };
}


function buildEx1TopTier(
  seats: TopologyPayload["seats"],
  topo: TopologyCfg,
  fuselageLeft: number,
  fuselageRight: number,
  lrus: TopologyPayload["lrus"],
  viewMode: ViewMode,
  chainColorByKey: Record<string, string> = {},
  lruLinks: NonNullable<TopologyPayload["lru_links"]> = [],
) {
  const fuseCx = (fuselageLeft + fuselageRight) / 2;
  const topTier = topo.top_tier ?? [];

  const mcusSet = new Set<number>();
  const portsByMcu = new Map<number, Set<number>>();
  for (const info of Object.values(seats)) {
    const m = info.mcu;
    if (m === undefined || m === null) continue;
    mcusSet.add(m);
    let set = portsByMcu.get(m);
    if (!set) { set = new Set(); portsByMcu.set(m, set); }
    set.add(info.port);
  }
  const mcuList = [...mcusSet].sort((a, b) => a - b);

  // When the backend provides lru_lookup data, derive the HeadEnd tier from
  // it directly (real designations like "MCU 001", "FS 001"). Fall back to
  // the seat-derived layout when lrus is empty (local replica or older CH).
  // Functional ordering: file servers and content servers/terminals first,
  // then aircraft-interface units, then everything else. ADBs (Server_Type
  // populated, e.g. Server_Type=FS) are children of FS in the data — we
  // demote them so they sort to the end, after the true top-of-tree boxes.
  // v2 (lru_lookup_ilopa): FS units now carry a parent (the AI is the real
  // root of the tree), so FS must NOT be demoted by having Server_Type.
  const headEndPriority = (acronym: string, serverType: string | null | undefined): number => {
    if (acronym.startsWith("FS")) return 10;
    if (acronym.startsWith("CS")) return 20;
    if (acronym.startsWith("CT")) return 30;
    if (acronym === "HCS08_AI") return 45;
    if (acronym.startsWith("AI")) return 40;
    if (serverType) return 90;          // has upstream parent → not top tier
    return 80;
  };
  const headEnd = lrus
    .filter((l) => l.category === "HeadEnd")
    .slice()
    .sort((a, b) => {
      const pa = headEndPriority(a.acronym, a.server_type);
      const pb = headEndPriority(b.acronym, b.server_type);
      if (pa !== pb) return pa - pb;
      return a.designation.localeCompare(b.designation);
    });
  // MCUs from ANY category: upstream miscategorises some as "Auxiliary"
  // (75G: MCU 002/003, 75Y: MCU 004) — restricting to HeadEnd dropped their
  // boxes and pushed their port groups into the fallback slot.
  const mcuLrus = lrus
    .filter((l) => l.acronym === "MCU")
    .slice()
    .sort((a, b) => a.designation.localeCompare(b.designation));
  // HCS08 rows (AI 1 HCS08 / IS 1 HCS08) are the parent unit's internal
  // microcontroller module, not a network box — placeholder IPs, no ports,
  // no children. Hidden from the head-end tree; the module surfaces in the
  // right pane when its parent AI/IS is clicked (Sidebar). Re-seat as a
  // dedicated child box if Edwin links them per-fleet later.
  const dataLrus = headEnd.filter(
    (l) => l.acronym !== "MCU" && !l.acronym.includes("HCS08"),
  );
  // v2 tier classification (lru_lookup_ilopa):
  //  - chain servers (FS / IS): the server row — they now have AI parents.
  //  - leaves (CT / CP / MAS with a parent): terminal branches off the AI,
  //    drawn in the server row with no children.
  //  - aux (parentless, non-server): AI roots — top row.
  //  - subs (other parented LRUs): ADBs / SEBAC — the sub row, as before.
  const isChainServer = (l: TopologyPayload["lrus"][number]): boolean =>
    l.acronym.startsWith("FS") ||
    (l.acronym.startsWith("IS") && !l.acronym.includes("HCS08"));
  const isLeafBranch = (l: TopologyPayload["lrus"][number]): boolean =>
    Boolean(l.server_type) &&
    (l.acronym.startsWith("CT") || l.acronym.startsWith("CP") ||
     l.acronym.startsWith("MAS"));
  // AI units stay in the top row even when parented — 75Y's AI 002 is
  // daisy-chained off AI 1 (Server_Type=AI, P0) but it's still a peer AI
  // unit, not sub-tier hardware; dropping it mid-canvas tangled every edge.
  const isAiUnit = (l: TopologyPayload["lrus"][number]): boolean =>
    l.acronym.startsWith("AI");
  const subDataLrus = dataLrus.filter(
    (l) => !isChainServer(l) && !isLeafBranch(l) && !isAiUnit(l) && !!l.server_type,
  );
  const useLruHeadEnd = headEnd.length > 0;

  // mcuList drives positions for both the MCU box row and the port rail.
  // When LRU data is present, derive from designations ("MCU 001" → 0); otherwise
  // stick with seat-derived indices.
  const mcuIdxFromDesignation = (d: string): number | null => {
    const m = d.match(/(\d+)\s*$/);
    return m ? parseInt(m[1], 10) - 1 : null;
  };
  // Union of LRU-derived and seat-derived MCUs: every MCU the seats
  // reference gets a real slot even if lru_lookup lacks its box row, and
  // every box gets a slot even if no seat references it.
  const effectiveMcuList: number[] = useLruHeadEnd
    ? [...new Set([
        ...mcuLrus
          .map((l) => mcuIdxFromDesignation(l.designation))
          .filter((n): n is number => n !== null),
        ...mcuList,
      ])].sort((a, b) => a - b)
    : mcuList;

  // Tier split: chain servers (FS / IS) form the connected tree row, joined
  // by the CT/MAS leaf branches; parentless units (AI roots, HCS08) sit in
  // the top row. AI→FS / AI→leaf / AI→MCU connectors are drawn from the
  // ilopa parent columns below.
  const conPrimaries = useLruHeadEnd ? dataLrus.filter(isChainServer) : [];
  const leafUnits = useLruHeadEnd
    ? dataLrus.filter((l) => !isChainServer(l) && isLeafBranch(l))
    : [];
  const auxPrimaries = useLruHeadEnd
    ? dataLrus.filter(
        (l) => !isChainServer(l) && !isLeafBranch(l) &&
               (!l.server_type || isAiUnit(l)),
      )
    : [];
  const AUX_Y = 18;
  const AUX_H = 50;
  // Connected row sits below the AUX row when LRU data drives the layout.
  // With v2 uplinks the gap fits the flush AI port chips + the orthogonal
  // cable channel + the children's entry-port tabs.
  const hasUplinks = useLruHeadEnd && dataLrus.some(
    (l) => (isChainServer(l) || isLeafBranch(l)) && !!l.server_type,
  );
  const conY = useLruHeadEnd ? (hasUplinks ? 128 : 62) : FS_Y_EX1;

  const nCon = useLruHeadEnd ? conPrimaries.length : topTier.length;
  const nLeaf = leafUnits.length;
  const nTop = useLruHeadEnd ? nCon + nLeaf : topTier.length;
  const nAux = auxPrimaries.length;
  const nSub = useLruHeadEnd ? subDataLrus.length : 0;
  const nMcu = useLruHeadEnd ? mcuLrus.length : mcuList.length;

  // Ports per chain parent (from the corrected-mapping colour keys) — sizes
  // the sub-tier slots so a box and its port row never overlap a sibling's.
  // One uniform socket footprint across every unit (item: port uniformity).
  const DPORT_W = 28;
  const DPORT_GAP = 6;
  const portsOfParent = (kind: string, num: number): number =>
    Object.keys(chainColorByKey).filter((k) => k.startsWith(`${kind}${num}-p`)).length;
  // Shrink boxes as the row gets crowded so wide-body fleets don't overflow.
  // Sized for "FS 001" + a type code — sub-labels are code-only now.
  const topBoxW =
    nTop >= 8 ? 96 :
    nTop >= 6 ? 102 : 110;
  const subBoxW = 92;  // ADBs etc. — narrower since there can be many

  const maxPortsPerMcu = Math.max(
    1,
    ...mcuList.map((m) => portsByMcu.get(m)?.size ?? 0),
  );
  const portW = 28;
  // Wide enough for the label AND its socket row.
  const mcuBoxW = Math.max(
    nMcu >= 5 ? 118 :
    nMcu >= 4 ? 126 :
    nMcu >= 3 ? 138 : 150,
    maxPortsPerMcu * (portW + 6) + 6,
  );

  const minTopGap = 14;
  const minMcuGap = 16;
  const minSubGap = 10;
  // Sub-tier slot: wide enough for the box AND its port row.
  // Long designations (e.g. seatbox rows on 75G) get a wider box.
  const subBoxWFor = (l: TopologyPayload["lrus"][number]): number =>
    Math.max(subBoxW, Math.ceil(l.designation.length * 6.6) + 16);
  const subSlotW = (l: TopologyPayload["lrus"][number]): number => {
    const m = l.designation.match(/^(FS|IS|ADB)\w*\s+0*(\d+)$/);
    const nPorts = m ? portsOfParent(m[1], parseInt(m[2], 10)) : 0;
    const portsW = nPorts > 0 ? nPorts * (DPORT_W + DPORT_GAP) - DPORT_GAP : 0;
    return Math.max(subBoxWFor(l), portsW);
  };
  const subSlots = useLruHeadEnd ? subDataLrus.map(subSlotW) : [];
  const auxBoxW = 104;
  const LEAF_W = 84;   // CT / MAS terminal branches — slimmer than servers
  // Server row = chain servers + leaf branches (mixed widths, one cursor).
  const conWidths = [
    ...Array(nCon).fill(topBoxW) as number[],
    ...Array(nLeaf).fill(LEAF_W) as number[],
  ];
  const conWidthSum = conWidths.reduce((a, b) => a + b, 0);
  const minConW = conWidths.length > 0
    ? conWidthSum + (conWidths.length + 1) * minTopGap
    : 0;
  const minAuxW = nAux > 0 ? nAux * auxBoxW + (nAux + 1) * minTopGap : 0;
  const minSubW = nSub > 0
    ? subSlots.reduce((a, b) => a + b, 0) + (nSub + 1) * minSubGap
    : 0;
  const minMcuW = nMcu * mcuBoxW + (nMcu + 1) * minMcuGap;
  const headerW = Math.max(minConW, minAuxW, minSubW, minMcuW);
  const auxGap = nAux > 0 ? (headerW - nAux * auxBoxW) / (nAux + 1) : 0;
  const subGap = nSub > 0 ? (headerW - nSub * subBoxW) / (nSub + 1) : 0;
  const mcuGap = nMcu > 0 ? (headerW - nMcu * mcuBoxW) / (nMcu + 1) : 0;
  const headerLeft = fuseCx - headerW / 2;

  // Server-row slot centres (servers first, then leaves).
  const conGap = conWidths.length > 0
    ? (headerW - conWidthSum) / (conWidths.length + 1)
    : 0;
  const conCenters: number[] = [];
  {
    let cx = headerLeft + conGap;
    for (const w of conWidths) {
      conCenters.push(cx + w / 2);
      cx += w + conGap;
    }
  }
  const fsX = (idx: number) => conCenters[idx];
  const leafX = (idx: number) => conCenters[nCon + idx];
  const auxX = (idx: number) =>
    headerLeft + auxGap + idx * (auxBoxW + auxGap) + auxBoxW / 2;
  const mcuLeftX = (m: number) => {
    const idx = effectiveMcuList.indexOf(m);
    // Fall back to last column for an unknown index so it still lands somewhere
    // visible (shouldn't happen with consistent data; guards against off-by-one).
    const pos = idx >= 0 ? idx : effectiveMcuList.length;
    return headerLeft + mcuGap + pos * (mcuBoxW + mcuGap);
  };

  const acronymKind = (a: string): Box["kind"] => {
    if (a === "MCU") return "mcu";
    if (a.startsWith("FS")) return "fs";
    // IS servers are data-tier (chain parents) — "ai" kind leaked them into
    // the MCU view where they overlapped the MCU boxes (73H).
    if (a.startsWith("IS")) return "fs";
    if (a.startsWith("AI") || a === "HCS08_AI") return "ai";
    if (a.startsWith("CT") || a.startsWith("CS")) return "fs";  // visually same tier
    return "ai";  // sensible default for OML/ACARS/IRS/FMS/etc.
  };

  // Secondary line = the type code only ("300-001"). Full unit names live in
  // the right pane after a click — repeating truncated names in every box was
  // noise ("Aircraft …").
  // Approx px-per-character at fontSize 9 (the secondary label). Used to budget
  // the label so it never overflows its box.
  const subCharPx = 5.2;
  const fitSub = (text: string, boxW: number): string => {
    const maxChars = Math.max(6, Math.floor((boxW - 10) / subCharPx));
    return text.length <= maxChars ? text : text.slice(0, maxChars - 1) + "…";
  };

  // Muted semantic palette: indigo = core AI roots, steel blue = servers,
  // violet = terminal branches, teal = distribution (ADB), slate = sockets.
  const topStyle: Record<string, { fill: string; stroke: string; sub: string }> = {
    fs: { fill: "#33689E", stroke: "#1F4265", sub: "File Server" },
    ai: { fill: "#4F46E5", stroke: "#3730A3", sub: "Aircraft Interface" },
  };

  // subCx is filled below when sub-tier boxes are positioned under their parents.
  const subCx = new Map<string, number>();
  // subConnectors collected during box building; merged into `connectors` later.
  const subConnectors: Connector[] = [];
  // Port annotations on connector ends (ilopa Server_Port / LRU_Port).
  const vLabels: ConnLabel[] = [];
  const pad3 = (t: string, n: number) => `${t} ${String(n).padStart(3, "0")}`;
  // Box anchors by designation — resolves iLOPA_Flag link endpoints and the
  // AI→MCU power uplinks across tiers. w = box width (edge-to-edge links).
  const anchorByDesignation =
    new Map<string, { cx: number; top: number; bottom: number; w: number }>();
  void subGap;  // retained for the headerW math; per-sub positioning replaces grid layout.
  // Y of the sub-tier row; pushed down when an FS-uplink port row is present.
  // The +48 clears the parents' flush data-port sockets (28) plus a cable gap.
  let subRowY = useLruHeadEnd ? conY + FS_H_EX1 + 48 : SUB_Y_EX1;
  const UPLINK_Y = conY + FS_H_EX1 - CHIP_H / 2;  // socket: straddles the border
  const UPLINK_W = 28;
  const UPLINK_H = CHIP_H;

  let topTierBoxes: Box[];
  if (useLruHeadEnd) {
    const lruBox = (
      l: TopologyPayload["lrus"][number], cx: number, y: number,
      w: number, h: number,
    ): Box => {
      const kind = acronymKind(l.acronym);
      const styleKey = kind === "fs" || kind === "ai" ? kind : "fs";
      const s = topStyle[styleKey];
      const typeCode = l.lru_type != null
        ? `${l.lru_type}-${String(l.lru_subtype ?? 0).padStart(3, "0")}`
        : "";
      const sub = typeCode;  // code only; the unit's full name lives in the right pane
      return {
        x: cx - w / 2, y, width: w, height: h,
        fill: s.fill, stroke: s.stroke,
        primaryLabel: l.designation, secondaryLabel: fitSub(sub, w),
        kind, ref: { primary: (mcuIdxFromDesignation(l.designation) ?? 0) + 1 },
      };
    };
    topTierBoxes = [
      // Connected tree row (FS / IS — real chain parents).
      ...conPrimaries.map((l, i) => lruBox(l, fsX(i), conY, topBoxW, FS_H_EX1)),
      // AUX row above: AI / CT / CP / HCS08 — connections not in the data yet.
      ...auxPrimaries.map((l, i) => lruBox(l, auxX(i), AUX_Y, auxBoxW, AUX_H)),
    ];
    // Position sub-tier (FS-children) boxes in ONE global row of slots —
    // each slot wide enough for the box and its port row — grouped by parent
    // so connectors don't cross. Per-parent centring overlapped siblings on
    // fleets where two parents each host multiple multi-port ADBs (76K).
    const parentDesignationFor = (l: TopologyPayload["lrus"][number]): string | null =>
      l.server_type && l.server_number !== null && l.server_number !== undefined
        ? `${l.server_type} ${String(l.server_number).padStart(3, "0")}`
        : null;

    // Leaf branches (CT / MAS): terminal boxes in the server row, keyed to
    // their parent AI for highlighting; Delta wants them visible with ports.
    leafUnits.forEach((l, i) => {
      const cx = leafX(i);
      const typeCode = l.lru_type != null
        ? `${l.lru_type}-${String(l.lru_subtype ?? 0).padStart(3, "0")}`
        : "";
      topTierBoxes.push({
        x: cx - LEAF_W / 2, y: conY, width: LEAF_W, height: FS_H_EX1,
        fill: "#7C68B0", stroke: "#524180",
        primaryLabel: l.designation, secondaryLabel: fitSub(typeCode, LEAF_W),
        kind: "leaf",
        ref: { primary: l.server_number ?? 1, secondary: l.server_port ?? undefined },
        parentKind: l.server_type ?? undefined,
      });
    });

    // Anchor points for sub→parent connectors, covering both rows.
    const primaryPosByDesignation = new Map<string, { cx: number; bottom: number }>();
    conPrimaries.forEach((l, i) => {
      primaryPosByDesignation.set(l.designation, { cx: fsX(i), bottom: conY + FS_H_EX1 });
      anchorByDesignation.set(l.designation,
        { cx: fsX(i), top: conY, bottom: conY + FS_H_EX1, w: topBoxW });
    });
    auxPrimaries.forEach((l, i) => {
      primaryPosByDesignation.set(l.designation, { cx: auxX(i), bottom: AUX_Y + AUX_H });
      anchorByDesignation.set(l.designation,
        { cx: auxX(i), top: AUX_Y, bottom: AUX_Y + AUX_H, w: auxBoxW });
    });
    leafUnits.forEach((l, i) => {
      anchorByDesignation.set(l.designation,
        { cx: leafX(i), top: conY, bottom: conY + FS_H_EX1, w: LEAF_W });
    });

    // Uplinks, daisy-chains and iLOPA_Flag secondaries all render in ONE
    // unified pass after the sub-tier is positioned — see "v2 edge
    // rendering" below (port chips under parents, edge-to-edge same-row
    // segments, dashed secondaries).

    const subsByParent = new Map<string, typeof subDataLrus>();
    for (const sub of subDataLrus) {
      const pd = parentDesignationFor(sub);
      if (pd === null) continue;
      if (!subsByParent.has(pd)) subsByParent.set(pd, []);
      subsByParent.get(pd)!.push(sub);
    }

    // Global row: only subs with a visible parent, ordered by parent column.
    const orderedSubs = [...subsByParent.entries()]
      .filter(([pd]) => primaryPosByDesignation.has(pd))
      .sort(([a], [b]) =>
        primaryPosByDesignation.get(a)!.cx - primaryPosByDesignation.get(b)!.cx)
      .flatMap(([, kids]) => kids);
    const subSiblingGap = 12;
    const slotWidths = orderedSubs.map(subSlotW);
    const rowW = slotWidths.reduce((a, b) => a + b, 0)
               + Math.max(0, orderedSubs.length - 1) * subSiblingGap;
    let cursor = fuseCx - rowW / 2;
    orderedSubs.forEach((k, i) => {
      subCx.set(k.designation, cursor + slotWidths[i] / 2);
      cursor += slotWidths[i] + subSiblingGap;
    });

    // FS uplink ports: on ADB fleets each ADB hangs off a dedicated FS port
    // (verified: those ports carry nothing else, and no seat maps straight
    // to an FS). Render them as small port boxes under the FS so the full
    // path reads FS → port → ADB → port → SEB. Selecting one selects its
    // downstream ADB (1:1 relationship).
    const adbSubs = orderedSubs.filter(
      (l) => l.acronym.startsWith("ADB") && l.server_port != null,
    );
    const hasUplinkRow = adbSubs.length > 0;
    // ADB row below the uplink sockets: cable channel + entry tabs.
    if (hasUplinkRow) subRowY = UPLINK_Y + UPLINK_H + 26 + TAB_H / 2;

    subDataLrus.forEach((l) => {
      const cx = subCx.get(l.designation);
      if (cx === undefined) return;  // orphan: no visible parent → don't render
      const w = subBoxWFor(l);
      anchorByDesignation.set(l.designation,
        { cx, top: subRowY, bottom: subRowY + SUB_H_EX1, w });
      const isAdb = l.acronym.startsWith("ADB");
      // ADBs carry their own number (from the designation) so selection can
      // target the exact box; other sub-tier LRUs keep the parent reference.
      const ownNum = mcuIdxFromDesignation(l.designation);
      // No sub-label: the uplink is drawn explicitly (parent → port → this
      // box), so repeating "FS 1 · p3" as text was noise.
      topTierBoxes.push({
        x: cx - w / 2, y: subRowY, width: w, height: SUB_H_EX1,
        fill: isAdb ? "#0F766E" : "#475569",
        stroke: isAdb ? "#0B4F4A" : "#1E293B",
        primaryLabel: l.designation,
        secondaryLabel: "",
        kind: isAdb ? "adb" : "fs",
        ref: { primary: isAdb ? (ownNum ?? 0) + 1 : (l.server_number ?? 1) },
      });
    });

    // FS-uplink sockets: one per ADB, flush under the parent FS, ordered by
    // the parent's uplink port. Cables run FS socket → orthogonal lane →
    // the ADB's entry tab. No stubs, no floating labels.
    if (hasUplinkRow) {
      const byParent = new Map<string, typeof adbSubs>();
      for (const a of adbSubs) {
        const pd = parentDesignationFor(a);
        if (pd === null || !primaryPosByDesignation.has(pd)) continue;
        if (!byParent.has(pd)) byParent.set(pd, []);
        byParent.get(pd)!.push(a);
      }
      type UpEdge = { x1: number; x2: number; adbD: string; inPort: number | null };
      const upEdges: UpEdge[] = [];
      for (const [pd, kids] of byParent) {
        const pPos = primaryPosByDesignation.get(pd)!;
        const sorted = kids.slice().sort(
          (a, b) => (a.server_port ?? 0) - (b.server_port ?? 0),
        );
        const gap = 8;
        const totalW = sorted.length * UPLINK_W + (sorted.length - 1) * gap;
        sorted.forEach((a, i) => {
          const ux = pPos.cx - totalW / 2 + i * (UPLINK_W + gap) + UPLINK_W / 2;
          const adbNum = (mcuIdxFromDesignation(a.designation) ?? 0) + 1;
          topTierBoxes.push({
            x: ux - UPLINK_W / 2, y: UPLINK_Y, width: UPLINK_W, height: UPLINK_H,
            fill: SOCKET_FILL, stroke: SOCKET_STROKE,
            primaryLabel: `P${a.server_port}`,
            secondaryLabel: "",
            kind: "uport",
            ref: { primary: adbNum, secondary: a.server_port ?? 0 },
          });
          const acx = subCx.get(a.designation);
          if (acx !== undefined) {
            upEdges.push({
              x1: ux, x2: acx, adbD: a.designation,
              inPort: a.lru_port ?? null,
            });
          }
        });
      }
      // Entry tabs on the ADBs' top edges, then lane-routed cables.
      const chTop = UPLINK_Y + UPLINK_H;
      const tabTop = subRowY - TAB_H / 2;
      for (const e of upEdges) {
        if (e.inPort === null) continue;
        const adbNum = (mcuIdxFromDesignation(e.adbD) ?? 0) + 1;
        topTierBoxes.push({
          x: e.x2 - TAB_W / 2, y: tabTop, width: TAB_W, height: TAB_H,
          fill: SOCKET_FILL, stroke: SOCKET_STROKE,
          primaryLabel: `P${e.inPort}`, secondaryLabel: "",
          kind: "inport", hostKind: "adb",
          ref: { primary: adbNum }, view: "data",
        });
      }
      const lanes = assignLanes(upEdges);
      upEdges.forEach((e, i) => {
        const laneY = chTop + 8 + lanes[i] * 7;
        const y2 = e.inPort !== null ? tabTop : subRowY;
        subConnectors.push({
          x1: e.x1, y1: chTop, x2: e.x2, y2,
          stroke: CONN_STROKE, width: CONN_WIDTH, opacity: CONN_OPACITY,
          conn: "sub",
          path: orthPath(e.x1, chTop, e.x2, y2, laneY),
        });
      });
    }

    // Direct parent → sub connectors for subs WITHOUT an uplink port box
    // (non-ADB sub-tier LRUs, or ADB rows missing Server_Port).
    for (const sub of subDataLrus) {
      if (hasUplinkRow && sub.acronym.startsWith("ADB") && sub.server_port != null) {
        continue;  // routed through its uplink port box above
      }
      const pd = parentDesignationFor(sub);
      if (pd === null) continue;
      const pPos = primaryPosByDesignation.get(pd);
      if (pPos === undefined) continue;
      const cx = subCx.get(sub.designation);
      if (cx === undefined) continue;
      subConnectors.push({
        x1: pPos.cx, y1: pPos.bottom,
        x2: cx,      y2: subRowY,
        stroke: CONN_STROKE, width: CONN_WIDTH, opacity: CONN_OPACITY, conn: "sub",
        // Lane just above the sub row so the run never cuts through the
        // server row when the parent sits in the top (AI) row.
        path: orthPath(pPos.cx, pPos.bottom, cx, subRowY, subRowY - 12),
      });
    }
  } else {
    topTierBoxes = topTier.map((t, i) => {
      const s = topStyle[t.kind] ?? topStyle.fs;
      const cx = fsX(i);
      return {
        x: cx - topBoxW / 2, y: FS_Y_EX1, width: topBoxW, height: FS_H_EX1,
        fill: s.fill, stroke: s.stroke,
        primaryLabel: t.label, secondaryLabel: "",  // fallback layout has no type codes
        kind: t.kind === "fs" ? "fs" : "ai",
        ref: { primary: t.idx + 1 },
      };
    });
  }

  const mcuBoxFills: Record<number, string> = {
    0: "#F5B7B1", 1: "#AED6F1", 2: "#A9DFBF", 3: "#F8C471", 4: "#A2D9CE",
  };
  // MCUs whose power parent is a data-tier server (IS fleets) render BELOW
  // that server so the MCU view shows the real source; AI-parent fleets
  // keep the fixed row under the AI roots.
  const mcuParentInServerRow = useLruHeadEnd && mcuLrus.some(
    (l) => l.server_type && !l.server_type.toUpperCase().startsWith("AI"),
  );
  const mcuY = mcuParentInServerRow ? conY + FS_H_EX1 + CHIP_H + 36 : MCU_Y_EX1;
  const mcuPortY = mcuY + MCU_H_EX1 - PORT_H / 2;
  let mcuBoxes: Box[];
  if (useLruHeadEnd) {
    mcuBoxes = mcuLrus.map((l, i) => {
      const idx = mcuIdxFromDesignation(l.designation) ?? i;
      const stroke = topo.zone_colors[`${idx}-0`] ?? "#222";
      const typeCode = l.lru_type != null
        ? `${l.lru_type}-${String(l.lru_subtype ?? 0).padStart(3, "0")}`
        : "";
      const sub = typeCode;  // code only
      return {
        x: mcuLeftX(idx), y: mcuY, width: mcuBoxW, height: MCU_H_EX1,
        fill: mcuBoxFills[idx] ?? "#E5E7E9", stroke,
        primaryLabel: l.designation, secondaryLabel: fitSub(sub, mcuBoxW),
        kind: "mcu", ref: { primary: idx },
      };
    });
  } else {
    mcuBoxes = mcuList.map((m) => {
      const stroke = topo.zone_colors[`${m}-0`] ?? "#222";
      return {
        x: mcuLeftX(m), y: mcuY, width: mcuBoxW, height: MCU_H_EX1,
        fill: mcuBoxFills[m] ?? "#E5E7E9", stroke,
        primaryLabel: `MCU ${m + 1}`,  // display 1-based, matching designations
        secondaryLabel: "198-000",
        kind: "mcu", ref: { primary: m },
      };
    });
  }

  const connectors: Connector[] = [...subConnectors];

  // ---- v2 edge rendering: uplinks + daisy-chains + flag secondaries ------
  // Design rules (the "no floating port numbers" pass):
  //  · Cross-row edges leave the parent through a labelled PORT CHIP under
  //    its box (same grammar as the FS→ADB uplink ports); the child's own
  //    entry port is pinned at its top edge, offset toward the arriving
  //    line. Chips are ordered by child position so lines don't cross.
  //  · Same-row edges with a clear gap draw as a straight edge-to-edge
  //    segment at box mid-height, ports labelled above its middle.
  //  · Same-row edges with boxes in between (ADB rings) keep the shallow
  //    arc with a single P⇄P label at the peak.
  //  · Normal-row parents = solid; iLOPA_Flag rows = dashed.
  if (useLruHeadEnd) {
    type Edge = {
      childD: string; parentD: string;
      parentType: string;       // parent unit type ("AI", "IS", …)
      port: number | null;      // parent-side port
      inPort: number | null;    // child's own entry port
      dashed: boolean;
    };
    const edges: Edge[] = [];
    // "IS 1" has no exact box ("IS_ARM 001" / "IS_X86 001") — fall back to
    // an acronym-prefixed match so MAS/CT still attach on IS fleets.
    const resolveParentD = (type: string, num: number): string | null => {
      const exact = pad3(type, num);
      if (anchorByDesignation.has(exact)) return exact;
      const re = new RegExp(`^${type}\\w*\\s+0*${num}$`);
      for (const d of anchorByDesignation.keys()) {
        if (re.test(d)) return d;
      }
      return null;
    };
    for (const l of dataLrus) {
      if (!l.server_type || l.server_number === null || l.server_number === undefined) continue;
      if (subCx.has(l.designation)) continue;  // sub-tier routes via uports
      if (!anchorByDesignation.has(l.designation)) continue;
      const pd = resolveParentD(l.server_type, l.server_number);
      if (!pd || pd === l.designation) continue;
      edges.push({
        childD: l.designation, parentD: pd, parentType: l.server_type,
        port: l.server_port ?? null, inPort: l.lru_port ?? null, dashed: false,
      });
    }
    const seenPair = new Set<string>();
    for (const lk of lruLinks) {
      if (!lk.parent_type || lk.parent_num === null || lk.parent_num === undefined) continue;
      const pd = resolveParentD(lk.parent_type, lk.parent_num);
      if (!pd || !anchorByDesignation.has(lk.designation)) continue;
      // Self-referential rows (75G: "FS 002 ← FS 2") are data slips — a
      // zero-length cable would drop its port pill dead-centre ON the box.
      if (pd === lk.designation) continue;
      // Mirrored rows describe one physical cable (ADB rings) — dedupe.
      const pairKey = [lk.designation, pd].sort().join("|");
      if (seenPair.has(pairKey)) continue;
      seenPair.add(pairKey);
      edges.push({
        childD: lk.designation, parentD: pd, parentType: lk.parent_type,
        port: lk.parent_port ?? null, inPort: lk.lru_port ?? null, dashed: true,
      });
    }

    // Split same-row vs cross-row.
    const CHIP_W = 28;
    const CHIP_GAP = 8;
    const chipY = AUX_Y + AUX_H - CHIP_H / 2;  // socket: straddles the border
    // parentD → port → edges (a chip fans out to every child on its port).
    const chipPlan = new Map<string, Map<number | null, Edge[]>>();

    const sameRowEdges: Edge[] = [];
    for (const e of edges) {
      const a = anchorByDesignation.get(e.childD)!;
      const b = anchorByDesignation.get(e.parentD)!;
      if (Math.abs(a.top - b.top) < 1) {
        // -- same row: rendered after the chips/tabs exist, so routing can
        //    see what actually occupies the space above the row -----------
        sameRowEdges.push(e);
      } else if (b.top <= AUX_Y + 1 && b.top < a.top) {
        // -- cross-row from a top-row parent → route through a port chip ---
        let ports = chipPlan.get(e.parentD);
        if (!ports) { ports = new Map(); chipPlan.set(e.parentD, ports); }
        const list = ports.get(e.port) ?? [];
        list.push(e);
        ports.set(e.port, list);
      } else {
        // -- cross-row from a lower-tier parent (rare) — direct line -------
        const [hi, lo] = a.top < b.top ? [a, b] : [b, a];
        connectors.push({
          x1: hi.cx, y1: hi.bottom, x2: lo.cx, y2: lo.top,
          stroke: CONN_STROKE, width: CONN_WIDTH, opacity: CONN_OPACITY,
          conn: "link", dashed: e.dashed,
        });
        if (e.port !== null) {
          vLabels.push({
            x: hi.cx + (lo.cx - hi.cx) * 0.5 + 4,
            y: hi.bottom + (lo.top - hi.bottom) * 0.5,
            text: `P${e.port}`, view: "data",
          });
        }
      }
    }

    // Lay out the chips flush under each parent — ordered by mean child x
    // (minimises crossings) — and collect the cross-row cables they emit.
    type CrossEdge = {
      chipX: number; childD: string; inPort: number | null; dashed: boolean;
    };
    const crossEdges: CrossEdge[] = [];
    for (const [parentD, ports] of chipPlan) {
      const b = anchorByDesignation.get(parentD)!;
      const aiNum = parseInt(parentD.match(/(\d+)\s*$/)?.[1] ?? "1", 10);
      const groups = [...ports.entries()].map(([port, list]) => ({
        port, list,
        meanChildX:
          list.reduce((s, e) => s + anchorByDesignation.get(e.childD)!.cx, 0) /
          list.length,
      })).sort((g1, g2) => g1.meanChildX - g2.meanChildX);
      const totalW = groups.length * CHIP_W + (groups.length - 1) * CHIP_GAP;
      groups.forEach((g, i) => {
        const cx = b.cx - totalW / 2 + i * (CHIP_W + CHIP_GAP) + CHIP_W / 2;
        topTierBoxes.push({
          x: cx - CHIP_W / 2, y: chipY, width: CHIP_W, height: CHIP_H,
          fill: SOCKET_FILL, stroke: SOCKET_STROKE,
          primaryLabel: g.port !== null ? `P${g.port}` : "P?",
          secondaryLabel: "",
          kind: "aiport", ref: { primary: aiNum, secondary: g.port ?? 0 },
          parentKind: g.list[0].parentType,
          view: "data",
        });
        for (const e of g.list) {
          crossEdges.push({
            chipX: cx, childD: e.childD, inPort: e.inPort, dashed: e.dashed,
          });
        }
      });
    }

    // Entry tabs: one per (child, entry port) on the child's top edge —
    // shared when two cables arrive on the same physical port. A child's
    // tabs spread around its centre, ordered by mean source x.
    const tabX = new Map<string, number>();  // "childD|port" → tab centre x
    {
      const byChild = new Map<string, Map<number, number[]>>();
      for (const e of crossEdges) {
        if (e.inPort === null) continue;
        let ports = byChild.get(e.childD);
        if (!ports) { ports = new Map(); byChild.set(e.childD, ports); }
        const xs = ports.get(e.inPort) ?? [];
        xs.push(e.chipX);
        ports.set(e.inPort, xs);
      }
      for (const [childD, ports] of byChild) {
        const a = anchorByDesignation.get(childD)!;
        const childBox = topTierBoxes.find((bx) => bx.primaryLabel === childD);
        const hostKind =
          childBox && childBox.kind !== "inport" ? childBox.kind : "fs";
        const ordered = [...ports.entries()]
          .map(([port, xs]) => ({ port, mean: xs.reduce((s, v) => s + v, 0) / xs.length }))
          .sort((p1, p2) => p1.mean - p2.mean);
        const totalW = ordered.length * TAB_W + (ordered.length - 1) * 4;
        ordered.forEach((p, i) => {
          const cx = a.cx - totalW / 2 + i * (TAB_W + 4) + TAB_W / 2;
          tabX.set(`${childD}|${p.port}`, cx);
          topTierBoxes.push({
            x: cx - TAB_W / 2, y: a.top - TAB_H / 2, width: TAB_W, height: TAB_H,
            fill: SOCKET_FILL, stroke: SOCKET_STROKE,
            primaryLabel: `P${p.port}`, secondaryLabel: "",
            kind: "inport",
            hostKind,
            parentKind: childBox?.parentKind,
            ref: childBox?.ref ?? { primary: 1 },
            view: "data",
          });
        });
      }
    }

    // Route the cables: rounded orthogonal runs in allocated lanes so no
    // two horizontal segments overlap. Redundant (iLOPA_Flag) cables stay
    // dashed; solid and dashed share the same lane discipline.
    {
      const chTop = chipY + CHIP_H;
      const spans = crossEdges.map((e) => ({
        x1: e.chipX,
        x2: tabX.get(`${e.childD}|${e.inPort}`) ??
            anchorByDesignation.get(e.childD)!.cx,
      }));
      const lanes = assignLanes(spans);
      crossEdges.forEach((e, i) => {
        const a = anchorByDesignation.get(e.childD)!;
        const x2 = spans[i].x2;
        const y2 = tabX.has(`${e.childD}|${e.inPort}`) ? a.top - TAB_H / 2 : a.top;
        const laneY = chTop + 8 + lanes[i] * 7;
        connectors.push({
          x1: e.chipX, y1: chTop, x2, y2,
          stroke: CONN_STROKE, width: CONN_WIDTH, opacity: CONN_OPACITY,
          conn: "link", dashed: e.dashed,
          path: orthPath(e.chipX, chTop, x2, y2, laneY),
        });
      });
    }

    // Same-row links (peer daisy-chains, cross-links, ADB rings), routed
    // with awareness of everything drawn so far:
    //  · clear neighbours → straight edge-to-edge segment at mid-height;
    //  · boxes in between + clear space above → orthogonal over-route in
    //    an allocated lane (rack-schematic style);
    //  · boxes in between AND occupied space above (76K ADB rings under
    //    the uplink channel) → the shallow arc, as before.
    {
      const items = sameRowEdges.map((e) => {
        const a = anchorByDesignation.get(e.childD)!;
        const b = anchorByDesignation.get(e.parentD)!;
        const lo = Math.min(a.cx, b.cx);
        const hi = Math.max(a.cx, b.cx);
        const blocked = [...anchorByDesignation.values()].some(
          (o) =>
            Math.abs(o.top - a.top) < 1 &&
            o.cx > lo + 1 && o.cx < hi - 1,
        );
        // Chips / tabs / boxes intersecting the band above the row within
        // the span would collide with an over-route (entry tabs also imply
        // a cable channel there).
        const clearAbove = !topTierBoxes.some((bx) =>
          bx.y + bx.height > a.top - 42 && bx.y < a.top - 1 &&
          bx.x + bx.width > lo && bx.x < hi);
        return { e, a, b, blocked, clearAbove };
      });
      const over = items.filter((it) => it.blocked && it.clearAbove);
      const overLanes = assignLanes(over.map((it) => ({ x1: it.a.cx, x2: it.b.cx })));
      let overIdx = 0;
      // A pair can carry several physical cables (daisy-chain + redundant
      // link) — fan their parallel segments out so they don't coincide.
      const pairSeen = new Map<string, number>();
      for (const it of items) {
        const { e, a, b } = it;
        if (!it.blocked) {
          const pairKey = [e.childD, e.parentD].sort().join("|");
          const nth = pairSeen.get(pairKey) ?? 0;
          pairSeen.set(pairKey, nth + 1);
          const [L, R] = a.cx < b.cx ? [a, b] : [b, a];
          const midY = (a.top + a.bottom) / 2 + nth * 8;
          connectors.push({
            x1: L.cx + L.w / 2, y1: midY, x2: R.cx - R.w / 2, y2: midY,
            stroke: CONN_STROKE, width: CONN_WIDTH, opacity: CONN_OPACITY,
            conn: "link", dashed: e.dashed,
          });
          const leftPort  = a.cx < b.cx ? e.inPort : e.port;
          const rightPort = a.cx < b.cx ? e.port : e.inPort;
          const text =
            leftPort !== null && rightPort !== null
              ? `P${leftPort} ⇄ P${rightPort}`
              : leftPort !== null ? `P${leftPort}`
              : rightPort !== null ? `P${rightPort}` : null;
          if (text) {
            // Inline ON the cable. A pill may bridge the two box EDGES like
            // a coupling badge (≤8px overhang each side = border padding
            // only), but never run over titles or sockets — narrowbody FS
            // boxes sit ~14px apart, where the full "P2 ⇄ P1" pill would
            // swallow both interiors. Degrade: full text → compact "2⇄1" →
            // no pill (the ports stay in the sidebar's Redundant links).
            const gapL = L.cx + L.w / 2;
            const gapR = R.cx - R.w / 2;
            const fits = (t: string) => t.length * 5.2 + 10 <= gapR - gapL + 16;
            const compact =
              leftPort !== null && rightPort !== null
                ? `${leftPort}⇄${rightPort}`
                : text.replace(/^P/, "");
            const shown = fits(text) ? text : fits(compact) ? compact : null;
            if (shown) {
              vLabels.push({
                x: (gapL + gapR) / 2,
                y: midY + 3,
                text: shown, view: "data", anchor: "middle",
                tone: e.dashed ? "redundant" : undefined,
              });
            }
          }
        } else if (it.clearAbove) {
          const laneY = a.top - 16 - overLanes[overIdx++] * 9;
          connectors.push({
            x1: a.cx, y1: a.top, x2: b.cx, y2: b.top,
            stroke: CONN_STROKE, width: CONN_WIDTH, opacity: CONN_OPACITY,
            conn: "link", dashed: e.dashed,
            path: orthPath(a.cx, a.top, b.cx, b.top, laneY),
          });
          if (e.inPort !== null && e.port !== null) {
            vLabels.push({
              x: (a.cx + b.cx) / 2, y: laneY + 3,
              text: `P${e.inPort} ⇄ P${e.port}`,
              view: "data", anchor: "middle",
              tone: e.dashed ? "redundant" : undefined,
            });
          }
        } else {
          connectors.push({
            x1: a.cx, y1: a.top, x2: b.cx, y2: b.top,
            stroke: CONN_STROKE, width: CONN_WIDTH, opacity: CONN_OPACITY,
            conn: "link", dashed: e.dashed,
            path: `M ${a.cx} ${a.top} Q ${(a.cx + b.cx) / 2} ${a.top - 26} ${b.cx} ${b.top}`,
          });
          if (e.inPort !== null && e.port !== null) {
            vLabels.push({
              x: (a.cx + b.cx) / 2, y: a.top - 26 + 3,
              text: `P${e.inPort} ⇄ P${e.port}`,
              view: "data", anchor: "middle",
              tone: e.dashed ? "redundant" : undefined,
            });
          }
        }
      }
    }
  }

  // ---- v2: AI → MCU power uplinks (MCU view only) ------------------------
  // Every MCU carries its server parent — typically ONE port fanning out to
  // all controllers (e.g. AI 1 · P3). Draw a socket flush under the parent
  // and one rounded L-run per MCU sharing the same bus lane: unselected they
  // merge into a single bus bar; selecting an MCU traces its own cable
  // cleanly back to the socket. The seat→MCU map stays on seat_lookup.
  if (useLruHeadEnd) {
    // "IS 1" has no exact box (IS_ARM 001 / IS_X86 001) — prefix-match, and
    // force the resolved parent visible in the MCU view (it IS the power
    // source there; without it the MCUs float parentless, e.g. 73H).
    const resolveParentDesignation = (type: string, num: number): string | null => {
      const exact = pad3(type, num);
      if (anchorByDesignation.has(exact)) return exact;
      const re = new RegExp(`^${type}\\w*\\s+0*${num}$`);
      for (const d of anchorByDesignation.keys()) {
        if (re.test(d)) return d;
      }
      return null;
    };
    type PowerGroup = {
      parentD: string; parentType: string; port: number | null; mcuIdxs: number[];
    };
    const powerGroups = new Map<string, PowerGroup>();
    for (const l of mcuLrus) {
      if (!l.server_type || l.server_number === null || l.server_number === undefined) continue;
      const parentD = resolveParentDesignation(l.server_type, l.server_number);
      if (!parentD) continue;
      const key = `${parentD}|${l.server_port ?? "x"}`;
      let g = powerGroups.get(key);
      if (!g) {
        g = { parentD, parentType: l.server_type, port: l.server_port ?? null, mcuIdxs: [] };
        powerGroups.set(key, g);
      }
      g.mcuIdxs.push(mcuIdxFromDesignation(l.designation) ?? 0);
    }
    // Socket chips flush under each parent, side by side when a parent
    // feeds more than one power port.
    const groupsByParent = new Map<string, PowerGroup[]>();
    for (const g of powerGroups.values()) {
      const list = groupsByParent.get(g.parentD) ?? [];
      list.push(g);
      groupsByParent.set(g.parentD, list);
    }
    const CHIP_W = 28;
    const busY = mcuY - 22;
    for (const [parentD, groups] of groupsByParent) {
      const b = anchorByDesignation.get(parentD)!;
      // The parent may be a data-tier server (IS) — force it visible here.
      const parentBox = topTierBoxes.find((bx) => bx.primaryLabel === parentD);
      if (parentBox && parentBox.kind !== "ai" && parentBox.kind !== "mcu") {
        parentBox.view = "both";
      }
      const unitNum = parseInt(parentD.match(/(\d+)\s*$/)?.[1] ?? "1", 10);
      const mcuCx = (idx: number) => mcuLeftX(idx) + mcuBoxW / 2;
      groups.sort((g1, g2) =>
        Math.min(...g1.mcuIdxs.map(mcuCx)) - Math.min(...g2.mcuIdxs.map(mcuCx)));
      const totalW = groups.length * CHIP_W + (groups.length - 1) * 8;
      groups.forEach((g, i) => {
        const chipCx = b.cx - totalW / 2 + i * (CHIP_W + 8) + CHIP_W / 2;
        topTierBoxes.push({
          x: chipCx - CHIP_W / 2, y: b.bottom - CHIP_H / 2, width: CHIP_W, height: CHIP_H,
          fill: SOCKET_FILL, stroke: SOCKET_STROKE,
          primaryLabel: g.port !== null ? `P${g.port}` : "P?",
          secondaryLabel: "",
          kind: "aiport", ref: { primary: unitNum, secondary: g.port ?? 0 },
          parentKind: g.parentType,
          view: "mcu",
        });
        // One rounded L-run per MCU, all in the same bus lane: together they
        // read as one bus bar; individually each traces socket → its MCU.
        for (const idx of g.mcuIdxs) {
          connectors.push({
            x1: chipCx, y1: b.bottom + CHIP_H / 2, x2: mcuCx(idx), y2: mcuY,
            stroke: CONN_STROKE, width: CONN_WIDTH, opacity: CONN_OPACITY,
            conn: "power", nc: idx,
            path: orthPath(chipCx, b.bottom + CHIP_H / 2, mcuCx(idx), mcuY, busY),
          });
        }
      });
    }
  }

  // Legacy FS/AI→MCU "hop" connectors — fallback layout only. They're never
  // rendered (upstream links aren't in the DB yet), but if built on the
  // LRU-driven layout their JSON-slot positions land far right of the real
  // drawing and inflate the canvas width, shrinking the fit-to-width LOPA.
  if (!useLruHeadEnd) {
    const fsColor = "#4A90D9";
    const aiColor = "#7D3C98";
    topTier.forEach((t, ti) => {
      const topX = fsX(ti);
      const lineColor = t.kind === "fs" ? fsColor : aiColor;
      for (const m of mcuList) {
        const mcuX = mcuLeftX(m) + mcuBoxW / 2;
        connectors.push({
          x1: topX, y1: FS_Y_EX1 + FS_H_EX1, x2: mcuX, y2: mcuY,
          stroke: lineColor, width: 1.0, opacity: 0.30, conn: "hop", nc: m,
        });
      }
    });
  }

  const portBoxes: Box[] = [];
  const portCentreByKey: Record<string, number> = {};
  for (const m of mcuList) {
    const portsHere = [...portsByMcu.get(m)!].sort((a, b) => a - b);
    const n = portsHere.length;
    const gap = (mcuBoxW - n * portW) / (n + 1);
    const base = mcuLeftX(m);
    // Sockets flush on the MCU's bottom edge — containment says "mine".
    portsHere.forEach((port, i) => {
      const cx = base + gap + i * (portW + gap) + portW / 2;
      const colour = topo.zone_colors[`${m}-${port}`] ?? "#888888";
      portBoxes.push({
        x: cx - portW / 2, y: mcuPortY, width: portW, height: PORT_H,
        fill: colour, stroke: "#1E293B",
        primaryLabel: `P${port}`, secondaryLabel: "",
        kind: "port", ref: { primary: m, secondary: port },
      });
      portCentreByKey[`${m}-${port}`] = cx;
    });
  }

  // ------------------------------------------------------------------
  // Data-side port row + chain rails (Data view).
  // When seats carry parsed chain data ("Seatbox on FS/ADB/IS n, Port p,
  // SEB s"), each chain parent gets dedicated PORT BOXES below it — same
  // visual grammar as the MCU view's port rail — filled with the chain's
  // seat colour. Rails drop from each port box to the seat grid.
  // Fleets without chain data fall back to cosmetic every-primary rails.
  // ------------------------------------------------------------------
  const hasSubTier = useLruHeadEnd && subDataLrus.length > 0;
  const mcuBottom  = mcuPortY + PORT_H;
  let dataBottom = hasSubTier ? subRowY + SUB_H_EX1 : conY + FS_H_EX1;

  if (viewMode !== "mcu") {  // data + avail share the chain head-end
    const chainedNums = new Map<string, Set<number>>();
    for (const s of Object.values(seats)) {
      if (!s.chain_kind || s.chain_num === undefined) continue;
      let set = chainedNums.get(s.chain_kind);
      if (!set) { set = new Set(); chainedNums.set(s.chain_kind, set); }
      set.add(s.chain_num);
    }
    // "FS 001" → (FS, 1); "IS_ARM 001" → (IS, 1); "ADB 002" → (ADB, 2).
    // Deliberately misses "AI 001" (AI isn't a chain parent kind) and
    // "IS 1 HCS08" (doesn't end in the number).
    const parentOfLabel = (label: string): { kind: string; num: number } | null => {
      const m = label.match(/^(FS|IS|ADB)\w*\s+0*(\d+)$/);
      return m ? { kind: m[1], num: parseInt(m[2], 10) } : null;
    };
    const hasChainData = chainedNums.size > 0;
    const railBoxes = topTierBoxes.filter((b) => {
      if (!hasChainData) return b.y === conY;  // cosmetic fallback
      const p = parentOfLabel(b.primaryLabel);
      if (!p) return false;
      return chainedNums.get(p.kind)?.has(p.num) ?? false;
    });

    if (hasChainData) {
      // Data-chain sockets sit flush on their own parent's bottom edge —
      // containment replaces the old stub lines. The chain colours already
      // tie each socket to its seats.
      const DPORT_H = CHIP_H;
      const dportGap = DPORT_GAP;
      let maxBottom = 0;
      // De-dup: IS servers render as two primary boxes (IS_ARM + IS_X86 for
      // the same IS 1) — emit one port row per (kind, num), under the first
      // matching box.
      const emitted = new Set<string>();
      for (const box of railBoxes) {
        const p = parentOfLabel(box.primaryLabel);
        if (!p) continue;
        const parentId = `${p.kind}${p.num}`;
        if (emitted.has(parentId)) continue;
        emitted.add(parentId);
        const keys = Object.keys(chainColorByKey)
          .filter((k) => k.startsWith(`${parentId}-p`))
          .sort((a, b) => parseInt(a.split("-p")[1], 10) - parseInt(b.split("-p")[1], 10));
        if (keys.length === 0) continue;
        const cx = box.x + box.width / 2;
        const dportY = box.y + box.height - DPORT_H / 2;
        const totalW = keys.length * DPORT_W + (keys.length - 1) * dportGap;
        keys.forEach((k, i) => {
          const port = parseInt(k.split("-p")[1], 10);
          const bx = cx - totalW / 2 + i * (DPORT_W + dportGap);
          topTierBoxes.push({
            x: bx, y: dportY, width: DPORT_W, height: DPORT_H,
            fill: chainColorByKey[k], stroke: "#1E293B",
            primaryLabel: `P${port}`,
            secondaryLabel: "",
            kind: "dport",
            ref: { primary: p.num, secondary: port },
          });
        });
        maxBottom = Math.max(maxBottom, dportY + DPORT_H);
      }
      dataBottom = Math.max(dataBottom, maxBottom);
    }
    // No chain data → no port sockets and no rails. Every drawn element must
    // be backed by a table row (lru_lookup / SEB_Port_Mapping / seat_lookup);
    // the old cosmetic "every-primary rail" fallback implied links that
    // aren't in the data, so it was removed.
  }

  // Pad 64: the fuselage nose arc rises 50px above the seat grid — anything
  // less and it overlaps the port row (the 76L aesthetic bug).
  const headerH = viewMode === "mcu"
    ? mcuBottom + 64
    : dataBottom + 64;

  return {
    topTierBoxes: [...topTierBoxes, ...mcuBoxes],
    portBoxes,
    connectors,
    portCentreByKey,
    labels: vLabels,
    headerH,
  };
}


function buildCabinGrid(
  cabin: string,
  layout: Layout,
  masterGridW: number,
  gridLeft: number,
): CabinGrid {
  const cellW = layout.canvas.cell_w;
  const cabinLayout = layout.cabins[cabin]?.layout ?? layout.columns.master;
  const nSeats = cabinLayout.filter((c) => c !== "|").length;
  const nAisles = cabinLayout.filter((c) => c === "|").length;

  let gap: number;
  if (nAisles > 0) {
    const stretched = (masterGridW - nSeats * cellW) / nAisles;
    gap = Math.min(cellW * 1.2, stretched);
  } else {
    gap = 0;
  }
  const totalW = nSeats * cellW + nAisles * gap;
  const offset = Math.max(0, (masterGridW - totalW) / 2);

  const colX: Record<string, number> = {};
  const aisles: number[] = [];
  let curX = gridLeft + offset;
  for (const c of cabinLayout) {
    if (c === "|") {
      aisles.push(curX + gap / 2);
      curX += gap;
    } else {
      colX[c] = curX;
      curX += cellW;
    }
  }
  return { colX, aisles };
}


export function seatColor(info: Seat, topo: TopologyCfg): string {
  const colors = topo.zone_colors;
  const key =
    topo.type === "nc-port"
      ? `${info.nc ?? ""}-${info.port}`
      : `${info.mcu ?? ""}-${info.port}`;
  return colors[key] ?? "#888888";
}


export function sebShort(info: Seat): string {
  const s = (info.seb_number ?? "").replace(/~/g, "");
  return s.length > 6 ? s.slice(-6) : s;
}
